import { api, ApiError } from '../lib/api.js';
import { el } from '../lib/ui.js';
import { familyGroups } from '../lib/family-groups.js';

type ScreenTools = { screen: (...nodes: Array<Node | null>) => void; testata: (title: string) => HTMLElement };

export function familyGroupSelect(value: string | null): HTMLSelectElement {
  const select = el('select', { class: 'scrivi-testo', id: 'person-group' },
    ...familyGroups.map(([key, label]) => el('option', { value: key }, label)));
  select.value = value ?? '';
  return select;
}

export async function renderPersonEditor(ui: ScreenTools, id?: string): Promise<void> {
  const person = id ? await api.get<{
    displayName: string; relation: string | null; birth: string | null; familyGroup: string | null; sex: 'M' | 'F' | null;
  }>(`/api/people/${id}`) : null;
  const name = el('input', { class: 'scrivi-testo', id: 'person-name', value: person?.displayName ?? '', required: '', maxlength: '250', autocomplete: 'off' });
  const relation = el('input', { class: 'scrivi-testo', id: 'person-relation', value: person?.relation ?? '', maxlength: '250', 'aria-describedby': 'relation-help', autocomplete: 'off' });
  const birth = el('input', { class: 'scrivi-testo', id: 'person-birth', value: person?.birth ?? '', placeholder: 'Es. 1952 oppure 10/02/1952', 'aria-describedby': 'birth-help' });
  const group = familyGroupSelect(person?.familyGroup ?? null);
  const sex = el('select', { class: 'scrivi-testo', id: 'person-sex', 'aria-describedby': 'sex-help' }, el('option', { value: '' }, 'Non indicato'), el('option', { value: 'M' }, 'Maschile'), el('option', { value: 'F' }, 'Femminile'));
  sex.value = person?.sex ?? '';
  group.setAttribute('aria-describedby', 'group-help');
  const status = el('p', { class: 'esito', role: 'status', 'aria-live': 'polite' });
  const field = (title: string, control: HTMLElement, ...help: Node[]): HTMLElement =>
    el('div', { class: 'correction-field' }, el('label', { for: control.id }, title), control, ...help);
  const save = el('button', { type: 'submit', class: 'btn-verde' }, id ? 'Salva le modifiche' : 'Aggiungi questa persona');
  const form = el('form', { class: 'correction-form carta' },
    el('div', { class: 'correction-fields' },
      field('Nome', name),
      field('Sesso · facoltativo', sex, el('p', { class: 'field-help', id: 'sex-help' }, 'Nei legami genitore–figlio già registrati distingue padre e madre. Se non è indicato, il ramo usa il nome del genitore. Non crea nuovi legami.')),
      field('Sottotitolo · chi è per te', relation,
        el('p', { class: 'field-help', id: 'relation-help' }, 'È facoltativo. Lascia vuoto per non mostrare nessun sottotitolo.'),
        el('button', { type: 'button', class: 'btn-quieto', click: () => { relation.value = ''; relation.focus(); } }, 'Togli il sottotitolo')),
      field('Gruppo nell’albero', group,
        el('p', { class: 'field-help', id: 'group-help' }, 'Il gruppo che scegli resta fisso. Sposta la persona nell’elenco, senza inventare o cambiare legami di parentela.')),
      field('Data di nascita · facoltativa', birth,
        el('p', { class: 'field-help', id: 'birth-help' }, 'Puoi scrivere solo l’anno, giorno/mese/anno o giorno/mese. Per toglierla, lascia vuoto.')),
    ),
    status,
    el('div', { class: 'correction-actions' }, save,
      el('a', { class: 'btn-secondario', href: id ? `#persona/${id}` : '#famiglia' }, 'Annulla')),
  );
  let saving = false;
  form.addEventListener('submit', (event) => {
    event.preventDefault();
    if (saving || !form.reportValidity()) return;
    saving = true;
    save.disabled = true;
    for (const input of [name, relation, birth, group, sex]) input.disabled = true;
    save.setAttribute('aria-busy', 'true');
    status.textContent = 'Salvo le tue modifiche…';
    void (async () => {
      try {
        const result = id
          ? await api.put(`/api/people/${id}`, { nome: name.value, relazione: relation.value, ...(birth.value !== (person?.birth ?? '') ? { nascita: birth.value } : {}), gruppo: group.value || null, ...(sex.value !== (person?.sex ?? '') ? { sesso: sex.value || null } : {}) })
          : await api.post<{ id: number }>('/api/people', { display_name: name.value, relation_to_mamma: relation.value, nascita: birth.value, family_group: group.value || null, sex: sex.value || null });
        if (form.isConnected) location.hash = `#persona/${id ?? (result as { id: number }).id}`;
      } catch (error) {
        status.textContent = error instanceof ApiError && error.status === 400
          ? 'Controlla il nome e la data: usa un anno oppure una data valida, per esempio 10/02/1952. Le modifiche sono ancora qui.'
          : 'Non sono riuscita a salvare. Le modifiche sono ancora qui: puoi riprovare.';
      } finally {
        saving = false; save.disabled = false; save.removeAttribute('aria-busy');
        for (const input of [name, relation, birth, group, sex]) input.disabled = false;
      }
    })();
  });
  const removal = el('section', { class: 'correction-removal' });
  const showRemove = (): void => {
    removal.replaceChildren(el('button', { class: 'btn-quieto', click: () => {
      removal.replaceChildren(
        el('p', {}, `Vuoi togliere ${person?.displayName ?? 'questa persona'} dalla famiglia e dall’albero? I racconti e le fotografie restano. Potrai rimetterla dall’elenco delle persone rimosse.`),
        el('div', { class: 'correction-actions' },
          el('button', { class: 'btn-secondario', click: async () => {
            if (saving) return;
            try { await api.post(`/api/people/${id}/togli`); if (removal.isConnected) location.hash = '#persone-rimosse'; }
            catch (error) { status.textContent = error instanceof ApiError && error.status === 400 ? 'Questa persona è protetta e non si può rimuovere.' : 'Non sono riuscita a rimuoverla: riprova.'; }
          } }, 'Sì, rimuovi la persona'),
          el('button', { class: 'btn-quieto', click: showRemove }, 'No, tienila'),
        ),
      );
      removal.querySelector('button')?.focus();
    } }, 'Rimuovi questa persona'));
  };
  if (id) showRemove();
  ui.screen(ui.testata(id ? 'Modifica la persona' : 'Aggiungi una persona'),
    el('p', { class: 'data-oggi' }, 'Nomi e parentele li decidi tu. Compila solo quello che sai.'), form, id ? removal : null);
}

export async function renderRemovedPeople(ui: ScreenTools): Promise<void> {
  const people = await api.get<Array<{ id: number; display_name: string }>>('/api/people/removed');
  const list = el('div', { class: 'pila' });
  const status = el('p', { class: 'esito', role: 'status' });
  for (const p of people) {
    const card = el('div', { class: 'carta removed-person' }, el('span', {}, p.display_name),
      el('button', { class: 'btn-secondario', click: async () => {
        try {
          await api.post(`/api/people/${p.id}/ripristina`);
          card.replaceChildren(el('span', {}, `${p.display_name} è di nuovo nella famiglia.`), el('a', { class: 'btn-quieto', href: `#persona/${p.id}` }, 'Apri la persona'));
        } catch { status.textContent = 'Non sono riuscita a ripristinare la persona: riprova.'; }
      } }, 'Rimetti nella famiglia'));
    list.append(card);
  }
  ui.screen(ui.testata('Persone rimosse'),
    el('p', { class: 'data-oggi' }, 'Rimuovere una persona non cancella racconti o foto. Qui puoi rimetterla al suo posto. Le schede unite a un’altra persona non sono doppioni da ripristinare.'),
    people.length ? list : el('p', { class: 'vuoto' }, 'Non ci sono persone rimosse.'), status,
    el('a', { class: 'btn-secondario', href: '#famiglia' }, 'Torna alla famiglia'));
}
